// AF-3.16 — Creator Command Center. Single consolidated view over the
// real Strategy -> Ideas -> Review -> Decision -> Production ->
// Distribution -> Measurement -> Learning pipeline, sourced entirely
// from AF-3.10's own CreatorCommandService aggregation (never a
// re-derivation of AF-3.1-3.9 state). The user never needs to
// understand which AF-3.x module owns which field.
//
// READY != APPROVED != PUBLISHED is enforced structurally: this page
// renders only status badges for readiness/approval -- no button here
// ever triggers an outbound action -- and shows the backend's own
// authorized-distribution-readiness phrase as plain status text only
// when distribution_status itself says so.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge, ReadinessBadge, ApprovalBadge, GovernanceBadge, GovernanceNote } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { useBrandContext } from '@/hooks/useBrandContext';
import { creatorCommandAPI } from '@/lib/intelligenceApi';
import { CheckCircle, Circle, CaretRight } from '@phosphor-icons/react';

const STAGES = [
  'OPPORTUNITY', 'RECOMMENDATION', 'ACTION_PLAN', 'CREATIVE_STRATEGY', 'IDEA', 'REVIEW', 'DECISION',
  'PRODUCTION', 'APPROVAL', 'DISTRIBUTION', 'MEASUREMENT', 'LEARNING', 'COMPLETE',
];

// AF-3.19 Phase 7 — minimal Strategy -> Ideas -> Review -> Decisions ->
// Production -> Distribution stepper so the pillar sequence itself (not
// just the detailed 13-stage pipeline below) reads as an obvious chain.
// Command Center aggregates every stage at once, so there is no single
// "current" stage here -- rendered as a plain, unhighlighted sequence.
const CHAIN_STAGES = ['Strategy', 'Ideas', 'Review', 'Decisions', 'Production', 'Distribution'];

function ChainProgress({ current }) {
  return (
    <div data-testid="creator-chain-progress" className="flex items-center gap-1.5 flex-wrap mb-3">
      {CHAIN_STAGES.map((stage, i) => (
        <span key={stage} className="flex items-center gap-1.5">
          <span className={`text-[10px] font-mono uppercase ${stage === current ? 'text-[var(--accent)] font-medium' : 'text-[var(--text-tertiary)]'}`}>{stage}</span>
          {i < CHAIN_STAGES.length - 1 && <CaretRight size={10} className="text-[var(--text-tertiary)]" />}
        </span>
      ))}
    </div>
  );
}

function StagePipeline({ currentStage }) {
  const idx = STAGES.indexOf(currentStage);
  return (
    <div data-testid="stage-pipeline" className="flex items-center gap-1 overflow-x-auto pb-2">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center gap-1 shrink-0">
          {i <= idx ? <CheckCircle size={14} weight="fill" className="text-[var(--accent)]" /> : <Circle size={14} className="text-[var(--text-placeholder)]" />}
          <span className={`text-[9px] font-mono uppercase ${i <= idx ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}>{s.replace(/_/g, ' ')}</span>
          {i < STAGES.length - 1 && <div className="w-4 h-px bg-[var(--border)] mx-1" />}
        </div>
      ))}
    </div>
  );
}

export default function CreatorCommandCenterPage() {
  const ctx = useBrandContext();
  const [command, setCommand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    creatorCommandAPI.create(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setCommand(r.data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div data-testid="creator-command-center-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Creator Command Center</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Strategy → Ideas → Review → Decision → Production → Distribution → Measurement → Learning, in one place.</p>
        </div>

        <ChainProgress />

        <BrandContextBar ctx={ctx} />

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && command && (
          <>
            <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 mb-4 shadow-[var(--shadow-card)]">
              <StagePipeline currentStage={command.current_stage} />
            </div>

            {!command.opportunity_id ? (
              <EmptyState title="NO PRODUCTION IN PROGRESS" description="Martian has not started a creative production pipeline for this entity yet." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="command-status-card">
                  <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Command status</p>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <StatusBadge value={command.command_status} />
                    <ReadinessBadge value={command.readiness} />
                  </div>
                  {command.governance_status && (
                    <div className="mb-2">
                      <GovernanceBadge value={command.governance_status} />
                      <GovernanceNote value={command.governance_status} />
                    </div>
                  )}
                  <p className="text-[11px] text-[var(--text-primary)] mt-2" data-testid="next-action-text">{command.next_action}</p>
                  {command.blockers?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[10px] font-mono uppercase text-[var(--error)]">Blockers</p>
                      <p className="text-[11px] text-[var(--text-secondary)]">{command.blockers.join(', ')}</p>
                    </div>
                  )}
                  {command.warnings?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[10px] font-mono uppercase text-[var(--warning)]">Warnings</p>
                      <ul className="text-[11px] text-[var(--text-secondary)] list-disc list-inside">
                        {command.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="production-card">
                  <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Production</p>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <StatusBadge value={command.production_status} />
                    {command.approval_required && <ApprovalBadge value={command.approval_status || 'PENDING'} />}
                  </div>
                  <p className="text-[10px] text-[var(--text-tertiary)]" data-testid="ready-approved-published-distinction">
                    READY (generated) ≠ APPROVED (reviewed) ≠ PUBLISHED (live). This asset is
                    {command.primary_production_asset_id ? ` currently ${command.production_status?.toLowerCase()}` : ' not yet in production'}
                    {command.approval_required ? `, approval ${command.approval_status?.toLowerCase() || 'pending'}` : ''}.
                  </p>
                  {command.video_generation_available !== undefined && (
                    <p className="text-[10px] text-[var(--text-secondary)] mt-2">
                      Video generation: <StatusBadge value={command.video_generation_available ? 'AVAILABLE' : 'UNAVAILABLE'} />
                      {command.video_generation_blocker && ` (${command.video_generation_blocker})`}
                    </p>
                  )}
                </div>

                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="distribution-card">
                  <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Distribution</p>
                  <StatusBadge value={command.distribution_status} />
                  {command.distribution_status === 'READY' && (
                    <p className="text-[11px] text-[var(--text-secondary)] mt-2" data-testid="ready-for-authorized-distribution">READY FOR AUTHORIZED DISTRIBUTION EXECUTION</p>
                  )}
                  <p className="text-[10px] text-[var(--text-tertiary)] mt-2">
                    {command.primary_distribution_target_id ? '1 primary target' : 'No target selected yet'}
                    {command.alternate_distribution_target_ids?.length > 0 ? `, ${command.alternate_distribution_target_ids.length} alternate(s)` : ''}
                  </p>
                </div>

                <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="measurement-card">
                  <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-2">Measurement</p>
                  <StatusBadge value={command.measurement_status} />
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(command.available_measurements || []).map((m, i) => (
                      <span key={i} className="text-[9px] font-mono text-[var(--neutral-text)] bg-[var(--neutral-bg)] border border-[var(--neutral-border)] rounded px-1.5 py-0.5">{m.metric}</span>
                    ))}
                    {(!command.available_measurements || command.available_measurements.length === 0) && (
                      <span className="text-[10px] text-[var(--text-tertiary)]">No measurements recorded yet</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
